import React from 'react';
import { InformationTableRow } from '@europrocurement/flexy-components';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';
import { Box, Checkbox, lighten, SxProps, Table, TableBody, useTheme } from '@mui/material';
import { LegalAnnoucement } from '@europrocurement/l2d-modules/modules/Dossiers/types';
import { type DossierArticleWithInvoice, useDossierProcesses } from '@europrocurement/l2d-modules';
import DossierArticleProcessMenu, {
    DossierArticleProcessMenuItemProps,
} from '../DossierArticleProcessMenu/DossierArticleProcessMenu';
import DossierArticleProcessStatus from '../DossierArticleProcessStatus/DossierArticleProcessStatus';
import DossierArticlesProcessIcons from '../DossierArticleProcessIcons/DossierArticleProcessIcons';

export type DossierArticlesProcessesListProps = {
    articles: DossierArticleWithInvoice[];
    dossier: DossierOpenSearch;
    validatorTypeId: number;
    legalAnnoucements: Array<LegalAnnoucement>;
    getItemActions?: (
        article: DossierArticleWithInvoice,
    ) => Array<DossierArticleProcessMenuItemProps>;
    selectedIds: number[];
    setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
};

const getKeyFromIndex = (index: number, prefix: string) => `${prefix}-${index}`;

const cellsSx: SxProps[] = [
    { width: '5%', display: 'table-cell' },
    { width: '5%' },
    { width: '40%' },
    { width: '25%' },
    { width: '15%' },
    { width: '10%' },
];

const DossierArticlesProcessesTaxList: React.FunctionComponent<DossierArticlesProcessesListProps> =
    function ({
        articles,
        dossier,
        validatorTypeId,
        legalAnnoucements,
        getItemActions = () => [],
        selectedIds,
        setSelectedIds,
    }) {
        const { palette } = useTheme();
        const { getProcessesByArticle } = useDossierProcesses();

        const tableHeadings = ['', 'N°', 'Démarches', 'Informations', 'Statuts', ''];

        const getSxFromIndex = (index: number): SxProps | undefined => {
            if (cellsSx && cellsSx[index]) {
                return cellsSx[index] as SxProps;
            }
            return undefined;
        };

        const headerCels = tableHeadings.map((header, headerIndex) => {
            let sx: SxProps = {
                border: 'none',
            };

            if (cellsSx) {
                const cellPropsSx = getSxFromIndex(headerIndex) as SxProps;
                sx = { ...sx, ...cellPropsSx };
            }
            return (
                <Box
                    sx={{
                        border: 'none',
                        padding: '2px 8px',
                        lineHeight: '1.6',
                        fontWeight: '500',
                        fontSize: '13px',
                        verticalAlign: 'middle',
                        color: lighten(palette.secondary.main, 0.5),
                        minWidth: '20px',
                        display: 'block',
                        ...sx,
                    }}
                    key={getKeyFromIndex(headerIndex, 'header')}
                >
                    {header}
                </Box>
            );
        });

        return (
            <Box
                sx={{
                    borderCollapse: 'separate',
                    borderSpacing: '0 4px',
                }}
            >
                <Box>
                    <Box
                        sx={{ width: '100%', display: 'flex' }}
                        data-testid="header-tax-list"
                    >
                        {headerCels}
                    </Box>
                </Box>
                <Table sx={{ width: '100%' }}>
                    <TableBody>
                        {articles
                            .filter((article) => article?.id)
                            .map((article, index) => {
                                const processes =
                                    getProcessesByArticle(dossier, validatorTypeId, article.id) ||
                                    undefined;
                                const process = processes[0] || undefined;

                                let updateValidatorAction:
                                    | DossierArticleProcessMenuItemProps
                                    | undefined = {} as DossierArticleProcessMenuItemProps;

                                if (getItemActions) {
                                    const itemsActions = getItemActions(article);
                                    if (itemsActions) {
                                        updateValidatorAction = itemsActions.find(
                                            (action) =>
                                                action.label === 'Modifier le statut valideur',
                                        );
                                    }
                                }

                                return (
                                    <InformationTableRow
                                        key={getKeyFromIndex(index + 1, 'row')}
                                        cels={[
                                            <Checkbox
                                                sx={{
                                                    transition: 'opacity ease-in-out 300ms',
                                                    width: '100%',
                                                }}
                                                checked={!!selectedIds.includes(article.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedIds((ids) => [
                                                            ...ids,
                                                            article.id,
                                                        ]);
                                                    } else {
                                                        setSelectedIds((ids) =>
                                                            ids.filter((id) => id !== article.id),
                                                        );
                                                    }
                                                }}
                                            />,
                                            index + 1,
                                            article.label,
                                            <DossierArticlesProcessIcons
                                                article={article}
                                                dossier={dossier}
                                                validatorTypeId={validatorTypeId}
                                                legalAnnoucements={legalAnnoucements}
                                            />,
                                            process !== undefined ? (
                                                <DossierArticleProcessStatus
                                                    statusFlag={process.statusFlag}
                                                    statusLabel={process.statusLabel}
                                                    onClick={() =>
                                                        updateValidatorAction?.onClick(article)
                                                    }
                                                />
                                            ) : null,
                                            <DossierArticleProcessMenu
                                                article={article}
                                                getItemActions={getItemActions}
                                            />,
                                        ]}
                                        cellsSx={cellsSx}
                                    />
                                );
                            })}
                    </TableBody>
                </Table>
            </Box>
        );
    };

export default DossierArticlesProcessesTaxList;
